import { collection, query, where, orderBy, getDocs, getDoc, doc } from 'firebase/firestore';
import { COLLECTIONS, Order, OrderItem, CourierPartner, Address, OrderTotals } from '@bookstore/shared';
import { getFirebaseFirestore, getFirebaseFunctions } from '@bookstore/shared/browser';
import { httpsCallable } from 'firebase/functions';

interface CreateOrderRequest {
  items: OrderItem[];
  shippingAddress:  Address;
  totalAmount: number;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  couponCode?: string;
  courierPartner: CourierPartner;
  shippingCharges: number;
  tax: number;
  totals: OrderTotals;
}


interface CreateOrderResponse {
  orderId: string;
  razorpayOrderId: string;
  amount: number;
}

interface VerifyPaymentRequest {
  razorpayOrderId: string;
  razorpayPaymentId: string;
  razorpaySignature: string;
}

interface VerifyPaymentResponse {
  success: boolean;
  orderId: string;
}

export class OrderService {
  private get db() {
    return getFirebaseFirestore();
  }

  private get functions() {
    return getFirebaseFunctions();
  }

  async getUserOrders(userId: string): Promise<Order[]> {
    const ordersQuery = query(
      collection(this.db, COLLECTIONS.ORDERS),
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    const snapshot = await getDocs(ordersQuery);
    return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Order));
  }

  async getOrderById(orderId: string): Promise<Order | null> {
    const orderDoc = await getDoc(doc(this.db, COLLECTIONS.ORDERS, orderId));
    if (!orderDoc.exists()) return null;
    return { id: orderDoc.id, ...orderDoc.data() } as Order;
  }

  async createOrder(data: CreateOrderRequest): Promise<CreateOrderResponse> {
    const createOrderFn = httpsCallable<CreateOrderRequest, CreateOrderResponse>(
    getFirebaseFunctions(),
      'createRazorpayOrder'
    );
    const result = await createOrderFn(data);
    return result.data;
  }

  async verifyPayment(data: VerifyPaymentRequest): Promise<VerifyPaymentResponse> {
    const verifyPaymentFn = httpsCallable<VerifyPaymentRequest, VerifyPaymentResponse>(
      getFirebaseFunctions(),
      'verifyRazorpayPayment'
    );
    const result = await verifyPaymentFn(data);
    return result.data;
  }

  async cancelOrder(orderId: string, initiatedBy: string, reason?: string): Promise<void> {
    const cancelOrderFn = httpsCallable(this.functions, 'cancelOrder');
    await cancelOrderFn({ orderId, reason, initiatedBy });
  }
}

export const orderService = new OrderService();